import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

/**
 * Merging .xmp and .xml sidecars into the files they describe.
 *
 * Survey first, and it is not a formality here. A sidecar merged onto the wrong
 * image writes somebody else's keywords and rating onto a photo, and afterwards
 * it looks exactly like data that was always there. The counts below are how
 * that gets caught before it happens rather than months later.
 */

type Survey = Awaited<ReturnType<typeof api.sidecarSurvey>>;

const EXPLANATION: Record<string, string> = {
  matched: 'Found exactly one file of that name in the same folder.',
  ambiguous:
    'Several files in that folder share the name. There is no correct answer, so nothing is merged.',
  orphan: 'No file of that name in that folder — the photo it describes was never uploaded.',
  contradicted:
    'The names line up but the sidecar disagrees about what it belongs to. Never merged.',
  unreadable: 'Could not be parsed as XMP or XML.',
  empty: 'Carries no rating, keywords, title or description worth merging.',
  'already-merged': 'Already merged into its file.',
};

export function SidecarPanel(): ReactNode {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [corroboratedOnly, setCorroboratedOnly] = useState(true);
  const [show, setShow] = useState<string | null>(null);

  const load = (): void => {
    setError(null);
    void api
      .sidecarSurvey()
      .then(setSurvey)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not survey sidecars'));
  };

  useEffect(load, []);

  const matched = (survey?.candidates ?? []).filter((c) => c.status === 'matched');
  const corroborated = matched.filter((c) => c.confidence === 'corroborated');
  const willMerge = corroboratedOnly ? corroborated : matched;

  const run = async (): Promise<void> => {
    if (willMerge.length === 0) return;
    if (
      !confirm(
        `Merge ${willMerge.length} sidecar(s) into their files? ` +
          'Every merge can be undone from the asset afterwards.',
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.sidecarMerge({ requireCorroboration: corroboratedOnly });
      setResult(
        `Merged ${res.merged} sidecar(s), adding ${res.tagsAdded} tag(s).` +
          (res.skipped > 0 ? ` ${res.skipped} skipped as filename-only.` : '') +
          (res.errors.length > 0 ? ` ${res.errors.length} failed.` : ''),
      );
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  if (error && !survey) return <div className="notice error">{error}</div>;
  if (!survey) return <p className="muted">Reading sidecars…</p>;

  const rows = show
    ? survey.candidates.filter((c) => c.status === show)
    : [];

  return (
    <div>
      <h3>Sidecar metadata</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Ratings, keywords and titles that editing tools wrote into .xmp and .xml files
        beside the originals. Merging makes them searchable. Nothing is merged where the
        match is ambiguous or where the sidecar disagrees about what it belongs to.
      </p>

      {error && <div className="notice error">{error}</div>}
      {result && <div className="notice success">{result}</div>}

      <table>
        <thead>
          <tr>
            <th>Outcome</th>
            <th style={{ width: 80 }}>Count</th>
            <th style={{ width: 90 }} />
          </tr>
        </thead>
        <tbody>
          {Object.entries(survey.byStatus)
            .sort((a, b) => b[1] - a[1])
            .map(([status, count]) => (
              <tr key={status}>
                <td>
                  <div>{status}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                    {EXPLANATION[status] ?? ''}
                  </div>
                </td>
                <td>{count}</td>
                <td>
                  <button
                    className="btn sm"
                    onClick={() => setShow(show === status ? null : status)}
                  >
                    {show === status ? 'Hide' : 'Show'}
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {rows.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Sidecar</th>
              <th>Folder</th>
              <th>Matched to</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((c) => (
              <tr key={c.sidecarAssetId}>
                <td style={{ overflowWrap: 'anywhere' }}>{c.sidecarFilename}</td>
                <td style={{ color: 'var(--text-dim)', overflowWrap: 'anywhere' }}>
                  {c.folderPath ?? '(top level)'}
                </td>
                <td style={{ overflowWrap: 'anywhere' }}>
                  {c.targetAssetId ? (
                    <Link to={`/asset/${c.targetAssetId}`} style={{ color: 'var(--accent)' }}>
                      {c.targetFilename}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                  {c.reason ??
                    (c.confidence === 'corroborated'
                      ? `verified by ${(c.matchedOn ?? []).join(', ')}`
                      : 'name only')}
                  {c.keywords && c.keywords.length > 0 && (
                    <div>keywords: {c.keywords.join(', ')}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="panel" style={{ marginTop: 14 }}>
        <label className="flex" style={{ alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={corroboratedOnly}
            onChange={(e) => setCorroboratedOnly(e.target.checked)}
            disabled={busy}
          />
          <span>
            Only merge sidecars that name what they belong to ({corroborated.length} of{' '}
            {matched.length})
          </span>
        </label>
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Most sidecars carry no identifying field and match on filename alone. That is
          usually right, and it is how the convention is meant to work — but it is a
          convention, not a guarantee, so it is offered separately.
        </p>

        <button
          className="btn primary"
          onClick={() => void run()}
          disabled={busy || willMerge.length === 0}
          style={{ marginTop: 6 }}
        >
          {busy ? 'Merging…' : `Merge ${willMerge.length} sidecar(s)`}
        </button>
      </div>
    </div>
  );
}
